import { useState, useRef, useCallback } from 'react';
import { downloadSampleTemplate, uploadLeads, downloadFailedLeads } from '../../services/api';
import './UploadExcel.css';

const ACCEPTED_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.ms-excel',                                           // .xls
];
const MAX_SIZE_MB = 10;
const MAX_SIZE_B = MAX_SIZE_MB * 1024 * 1024;

const PURPOSE_OPTIONS = [
  'Sales Outreach',
  'Marketing',
  'Partnership',
  'Demo Request',
  'Consultation',
  'Recruitment',
  'General Inquiry',
];

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatError(errors) {
  if (!errors || errors.length === 0) return '—';
  if (errors.length === 1) return errors[0];
  return `${errors[0]} +${errors.length - 1} more`;
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function UploadExcel() {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [fileError, setFileError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState(null);
  const [purpose, setPurpose] = useState('');
  const [purposeError, setPurposeError] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [exportingFailed, setExportingFailed] = useState(false);

  const inputRef = useRef(null);

  // ── Toast helper ─────────────────────────────────────────────────────────────
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ── File validation ───────────────────────────────────────────────────────────
  const validateFile = (f) => {
    if (!ACCEPTED_TYPES.includes(f.type) && !f.name.match(/\.(xlsx|xls)$/i)) {
      return 'Only Excel files (.xlsx, .xls) are accepted.';
    }
    if (f.size > MAX_SIZE_B) {
      return `File size exceeds the ${MAX_SIZE_MB} MB limit (your file: ${formatBytes(f.size)}).`;
    }
    return null;
  };

  const applyFile = (f) => {
    const err = validateFile(f);
    if (err) {
      setFileError(err);
      setFile(null);
    } else {
      setFileError(null);
      setFile(f);
    }
  };

  // ── Drag-and-drop ─────────────────────────────────────────────────────────────
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) applyFile(dropped);
  }, []); // eslint-disable-line

  const handleFileInput = (e) => {
    const picked = e.target.files[0];
    if (picked) applyFile(picked);
    e.target.value = '';
  };

  const handleRemoveFile = () => {
    setFile(null);
    setFileError(null);
  };

  // ── Download sample template ──────────────────────────────────────────────────
  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const response = await downloadSampleTemplate();
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      triggerDownload(blob, 'sample-template.xlsx');
      showToast('Sample template downloaded successfully');
    } catch (err) {
      showToast(err.message || 'Failed to download template', 'error');
    } finally {
      setDownloading(false);
    }
  };

  // ── Upload ────────────────────────────────────────────────────────────────────
  const handleUpload = async () => {
    let valid = true;
    if (!purpose.trim()) {
      setPurposeError('Please select or enter a purpose before uploading.');
      valid = false;
    } else {
      setPurposeError(null);
    }
    if (!file || !valid) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('purpose', purpose.trim());

      const response = await uploadLeads(formData);
      const result = response.data;
      setUploadResult(result);

      if (result.failedCount === 0) {
        showToast(`All ${result.savedCount} records uploaded successfully`);
      } else if (result.savedCount === 0) {
        showToast(`Upload failed — ${result.failedCount} records had errors`, 'error');
      } else {
        showToast(
          `${result.savedCount} saved, ${result.failedCount} failed — review the report below`,
          'warning'
        );
      }
    } catch (err) {
      showToast(err.message || 'Upload failed. Please try again.', 'error');
    } finally {
      setUploading(false);
    }
  };

  // ── Download failed records ───────────────────────────────────────────────────
  const handleDownloadFailed = async () => {
    if (!uploadResult?.failedRecords?.length) return;
    setExportingFailed(true);
    try {
      const response = await downloadFailedLeads(uploadResult.failedRecords);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      triggerDownload(blob, 'failed-records.xlsx');
      showToast('Failed records exported successfully');
    } catch (err) {
      showToast(err.message || 'Export failed. Please try again.', 'error');
    } finally {
      setExportingFailed(false);
    }
  };

  // ── Reset ─────────────────────────────────────────────────────────────────────
  const handleReset = () => {
    setFile(null);
    setFileError(null);
    setPurpose('');
    setPurposeError(null);
    setUploadResult(null);
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="ue-wrapper">

      {/* Toast */}
      {toast && (
        <div className={`toast toast-${toast.type}`}>
          {toast.type === 'success' ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          ) : toast.type === 'warning' ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          )}
          {toast.msg}
        </div>
      )}

      {/* Page header */}
      <div className="ue-header">
        <div className="ue-header-left">
          <h1 className="ue-title">Upload Excel</h1>
          <p className="ue-subtitle">
            Import lead data in bulk by uploading a filled Excel file.
            Use the sample template below as a starting point.
          </p>
        </div>
        <button
          className="btn btn-secondary ue-download-btn"
          onClick={handleDownloadTemplate}
          disabled={downloading}
          title="Download the pre-formatted import template"
        >
          {downloading ? (
            <span className="btn-spinner" />
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          )}
          {downloading ? 'Downloading…' : 'Download Sample Template'}
        </button>
      </div>

      {/* Purpose selector */}
      <div className="ue-purpose-section">
        <label className="ue-purpose-label" htmlFor="purpose-input">
          Upload Purpose <span className="ue-required">*</span>
        </label>
        <div className="ue-purpose-input-wrapper">
          <input
            id="purpose-input"
            className={`ue-purpose-input${purposeError ? ' ue-purpose-input--error' : ''}`}
            list="purpose-options"
            placeholder="Select or type a purpose…"
            value={purpose}
            onChange={(e) => {
              setPurpose(e.target.value);
              if (e.target.value.trim()) setPurposeError(null);
            }}
          />
          {purpose && (
            <button
              type="button"
              className="ue-purpose-clear"
              onClick={() => {
                setPurpose('');
                setPurposeError(null);
              }}
              title="Clear purpose"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
        <datalist id="purpose-options">
          {PURPOSE_OPTIONS.map((opt) => (
            <option key={opt} value={opt} />
          ))}
        </datalist>
        {purposeError && (
          <span className="ue-purpose-error">{purposeError}</span>
        )}
        <span className="ue-purpose-hint">
          Choose from the list or type a custom value.
        </span>
      </div>

      {/* Drop zone */}
      <div
        className={`ue-dropzone ${dragOver ? 'ue-dropzone--active' : ''} ${file ? 'ue-dropzone--has-file' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !file && inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && !file && inputRef.current?.click()}
        aria-label="Drop Excel file here or click to browse"
      >
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls"
          className="ue-file-input"
          onChange={handleFileInput}
        />

        {!file ? (
          <div className="ue-dropzone-empty">
            <div className="ue-drop-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
                strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="12" y1="18" x2="12" y2="12" />
                <line x1="9" y1="15" x2="15" y2="15" />
              </svg>
            </div>
            <p className="ue-drop-primary">
              Drag &amp; drop your Excel file here
            </p>
            <p className="ue-drop-secondary">
              or <span className="ue-browse-link">click to browse</span>
            </p>
            <p className="ue-drop-hint">
              Supported formats: <strong>.xlsx</strong>, <strong>.xls</strong> — max {MAX_SIZE_MB} MB
            </p>
          </div>
        ) : (
          <div className="ue-file-preview" onClick={(e) => e.stopPropagation()}>
            <div className="ue-file-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
                strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
            </div>
            <div className="ue-file-meta">
              <span className="ue-file-name">{file.name}</span>
              <span className="ue-file-size">{formatBytes(file.size)}</span>
            </div>
            <button
              className="ue-file-remove"
              onClick={handleRemoveFile}
              title="Remove file"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Validation error */}
      {fileError && (
        <div className="ue-file-error">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {fileError}
        </div>
      )}

      {/* Upload button */}
      <div className="ue-actions">
        <button
          className="btn btn-primary ue-upload-btn"
          onClick={handleUpload}
          disabled={!file || uploading}
        >
          {uploading ? (
            <><span className="btn-spinner" /> Uploading…</>
          ) : (
            <>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              Upload File
            </>
          )}
        </button>
      </div>

      {/* Upload result */}
      {uploadResult && (
        <div className="ue-result">
          <div className="ue-result-summary">
            <h3 className="ue-result-title">Upload Report</h3>
            <div className="ue-result-badges">
              <span className="ue-badge ue-badge--total">
                Total: {uploadResult.totalRows}
              </span>
              <span className="ue-badge ue-badge--success">
                Saved: {uploadResult.savedCount}
              </span>
              {uploadResult.failedCount > 0 && (
                <span className="ue-badge ue-badge--error">
                  Failed: {uploadResult.failedCount}
                </span>
              )}
            </div>
            <button className="btn btn-secondary ue-reset-btn" onClick={handleReset}>
              Upload Another
            </button>
          </div>

          {uploadResult.failedCount > 0 && (
            <div className="ue-failed-section">
              <div className="ue-failed-header">
                <h4 className="ue-failed-title">
                  Failed Records ({uploadResult.failedCount})
                </h4>
                <button
                  className="btn btn-secondary ue-export-btn"
                  onClick={handleDownloadFailed}
                  disabled={exportingFailed}
                >
                  {exportingFailed ? (
                    <><span className="btn-spinner" /> Exporting…</>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      Download Failed Records
                    </>
                  )}
                </button>
              </div>
              <p className="ue-export-hint">
                Download the failed records, correct the errors, and re-upload.
              </p>

              <div className="ue-table-wrap">
                <table className="ue-failed-table">
                  <thead>
                    <tr>
                      <th>Row #</th>
                      <th>First Name</th>
                      <th>Last Name</th>
                      <th>Email</th>
                      <th>Company</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadResult.failedRecords.map((r, idx) => (
                      <tr key={idx}>
                        <td className="ue-cell-row">{r.rowNumber ?? '—'}</td>
                        <td>{r.firstName || '—'}</td>
                        <td>{r.lastName || '—'}</td>
                        <td className="ue-cell-email">{r.email || '—'}</td>
                        <td>{r.companyName || '—'}</td>
                        <td>
                          <span className="ue-error-chip" title={r.errors?.join('\n')}>
                            {formatError(r.errors)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Instructions */}
      {!uploadResult && (
        <div className="ue-instructions">
          <h3 className="ue-instructions-title">How to import leads</h3>
          <ol className="ue-steps">
            <li>
              <span className="ue-step-num">1</span>
              <div className="ue-step-content">
                <strong>Download the sample template</strong>
                <p>Click <em>Download Sample Template</em> above to get the pre-formatted Excel file.</p>
              </div>
            </li>
            <li>
              <span className="ue-step-num">2</span>
              <div className="ue-step-content">
                <strong>Fill in your data</strong>
                <p>Complete all required columns in the template. Do not change column headers.</p>
              </div>
            </li>
            <li>
              <span className="ue-step-num">3</span>
              <div className="ue-step-content">
                <strong>Select a purpose</strong>
                <p>Choose the campaign / purpose for this batch from the dropdown above, or type a custom value.</p>
              </div>
            </li>
            <li>
              <span className="ue-step-num">4</span>
              <div className="ue-step-content">
                <strong>Upload the file</strong>
                <p>Drag &amp; drop the completed file into the zone above, or click to browse, then hit <em>Upload File</em>.</p>
              </div>
            </li>
          </ol>
        </div>
      )}

    </div>
  );
}
